"use client";

import { Carousel } from "@/components/shared/carousel";
import { BenefitsSection } from "@/components/shared/benefits_section";
import { EvidenceSection } from "@/components/shared/evidence_section";
import { AlternatingSection } from "@/components/shared/alternating_section";
import { useAnimation, useScroll } from "motion/react";
import { useEffect, useRef } from "react";
import { CloudflareStreamVideo } from "@/components/shared/CloudflareStreamVideo";
import { VideosWithFeatures } from "@/components/shared/videos_with_features";
import { QuoteCarousel, QuoteData } from "@/components/shared/quote_carousel";
import { ContentDownload } from "@/components/shared/content_download";
import { DynamicCaseStudies } from "@/components/shared/dynamic_case_studies";
import { IntegrationsCarouselCompact } from "@/components/shared/integrations_carousel";
import { useTranslations, useLocale } from "next-intl";
import { FeatureHeroSection } from "@/components/shared/feature_hero_section";
import { CaseStudyButton } from "@/components/shared/CaseStudyButton";
import {
    get__video_for_locale,
} from "@/constants/videos";

export default function Home() {
    const t = useTranslations();

    const { scrollY } = useScroll();
    const controlsArray = useRef([useAnimation(), useAnimation(), useAnimation()]);
    const sectionOffsets = useRef<number[]>([]);

    // Refs for feature sections
    const featureSectionRefs = {
        flashcards: useRef<HTMLDivElement>(null),
        mindmaps: useRef<HTMLDivElement>(null),
        tests: useRef<HTMLDivElement>(null)
    };

    // Example quotes data
    const exampleQuotes: QuoteData[] = [
        {
            text: "StudyStash has helped our teams to deliver consistent, high-quality teaching at scale. It's made a real difference in supporting students arriving from overseas, giving them the confidence to fully engage with modules throughout their transition to university.",
            author: "Prof. Matthew Leeke",
            title: "Deputy Pro-Vice-Chancellor (Student Experience), University of Birmingham",
            image: "/leeke-matthew-3.jpg",
            logo: "/uob_crest.png"
        },
        {
            text: "What I really like about StudyStash is that it simplifies the revision process, but still draws on all the neuroscience of learning.",
            author: "Benjamin Ellis",
            title: "Head of School (Education) of School of Sport, Exercise and Rehabilitation Sciences",
            image: "/quote_images/benjamin_ellis.jpg",
            logo: "/uob_crest.png"
        },
        {
            text: "The pilot is hugely exciting and offers us an excellent opportunity to explore how we can use education technologies to further enhance student support and the student experience at the University of Birmingham.",
            author: "Professor Deborah Longworth",
            title: "Pro-Vice-Chancellor (Education), University of Birmingham",
            image: "/deborah.png",
            logo: "/uob_crest.png"
        },
        {
            text: "Ben, Jonathan - let me say again that you and your team are doing something really special. I've been talking with a lot of universities about where they're at on their AI journey, and the kind of support you're providing isn't on any of their radars.",
            author: "Gary Guadagnolo",
            title: "Senior Research Director, EAB",
            image: "/gary.png",
            logo: "/EAB.png"
        }
    ];

    const RecommendedBlogs = [
        {
            title: "StudyStash featured on 9 to Thrive podcast",
            text: "Co-founder Jonathan Graham joins Nicola Gittins to share insights on building a business as a student.",
            imageUrl: "/case-studies/9-to-thrive.png",
            link: "/about/blogs/9-to-thrive-podcast"
        },
        {
            title: "StudyStash features in Uncensored",
            text: `The StudyStash team is redefining ${t("general.words.university")} learning by creating a ${t("general.words.personalised")} platform driven by ${t("general.words.neuroscience")}.`,
            imageUrl: "/case-studies/innovators_uncensored.jpg",
            link: "/about/blogs/innovators-uncensored"
        },
        {
            title: "StudyStash named as finalists for EdTechX",
            text: "West-Midlands based start-up StudyStash competes with Duolingo for the prestigious EdTechX award in London!",
            imageUrl: "/case-studies/EdTechX.png",
            link: "/about/blogs/edtechx-awards"
        }
    ];

    useEffect(() => {
        // Add hash scroll handling
        if (window.location.hash === '#video') {
            // Small delay to ensure proper scroll positioning
            setTimeout(() => {
                const videoSection = document.getElementById('video');
                if (videoSection) {
                    const headerOffset = -100;
                    const elementPosition = videoSection.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }, 100);
        }

        // Calculate offsets for each section
        sectionOffsets.current = sectionsRef.current.map((section) => {
            return section.offsetTop;
        });

        const unsubscribe = scrollY.onChange((latest) => {
            // Update animations
            controlsArray.current.forEach((controls, index) => {
                if (latest > sectionOffsets.current[index] - window.innerHeight / 1.5) {
                    controls.start({
                        opacity: 1,
                        transition: { duration: 0.5 }
                    });
                }
            });
        });

        return () => unsubscribe();
    }, [scrollY]);

    const sectionsRef = useRef<HTMLDivElement[]>([]);
    const locale = useLocale();

    return (
        <main className="marketing-root">
            <div className="pt-4 sm:pt-6 md:pt-8">
                <div className="flex w-full flex-col items-center">
                    <FeatureHeroSection
                        title="Safe, AI-Powered Learning,"
                        highlightedText="all within your LMS"
                        description={locale === 'en-us'
                            ? "We deliver measurable improvements to student engagement, DFW rates, faculty workload and college outcomes that matter."
                            : `We deliver measurable, evidence-based improvements in student ${t("general.words.attainment")} and confidence, reduced faculty workload, and ${t("general.words.university")} outcomes that matter.`}
                        buttonText="Join the pilot"
                        buttonHref="/pilot-program"
                        secondButtonText="Book a demo"
                        secondButtonHref="/book-a-demo"
                        showDemoModal={false}
                        category=""
                        icon=""
                        iconAlt=""
                    />

                    <Carousel />

                    <div className="relative w-full overflow-x-hidden">
                        <div id="video" className="relative mx-auto w-full max-w-4xl px-4 sm:px-6 py-12 sm:py-16 md:py-20">
                            <div className="w-full overflow-x-auto rounded-xl shadow-lg ring-1 ring-black/5 backdrop-blur-sm">
                                <CloudflareStreamVideo
                                    uid={get__video_for_locale(locale, "STUDENT_EXPLAINER_VIDEOS")}
                                    poster={"https://studystash.com/placeholder_thumbnail_video.png"}
                                />
                            </div>
                        </div>

                        <div className="absolute top-[300px] sm:top-[400px] right-0 pointer-events-none hidden lg:block">
	                            <img
	                                src="/left-right-lines.svg"
	                                alt="Illustration of the StudyStash teaching-learning loop"
	                                className="w-[200px] sm:w-[300px] h-auto opacity-50 sm:opacity-100"
	                                loading="lazy"
	                                decoding="async"
	                            />
                        </div>

                        <div className="container mx-auto px-4 lg:px-8">
                            <CaseStudyButton />
                        </div>

                        <BenefitsSection
                            showButton={true}
                            heading="We complete the teaching-learning loop"
                            subheading1="StudyStash works alongside your existing LMS - adding a layer of adaptive learning intelligence that boosts student engagement and outcomes."
                            subheading2={`Think of us as the missing link between what ${t("general.words.universities")} deliver and what students achieve.
We use evidence-backed ${t("general.words.neuroscience")} to create ${t("general.words.personalised")} learning pathways while building institutional capacity for better outcomes.`}
                            cards={[
                                {
                                    title: `For ${t("general.words.universities")}…`,
                                    description: "Make your LMS truly adaptive - automatically engage students, boost grades, and reduce dropouts.",
                                    image: "/for-universities-small.png",
                                    imageAlt: "University features",
                                    bgColor: "bg-ss-purple"
                                },
                                {
                                    title: `For ${t("general.words.lecturers")}…`,
                                    description: "Reduce your workload and receive insight reports that show exactly where your students are struggling.",
                                    image: "/for-lecturers-small.png",
                                    imageAlt: "Lecturer features",
                                    bgColor: "bg-ss-blue"
                                },
                                {
                                    title: "For students…",
                                    description: "Convert your course materials into personalised study tools, like flashcards and practice tests, with 24/7 support from our AI study tutor, StudyChat.",
                                    image: "/for-students-small.png",
                                    imageAlt: "Student features",
                                    bgColor: "bg-ss-green"
                                }
                            ]}
                            buttonText="See how it works"
                            buttonHref="/book-a-demo"
                        />
                    </div>

                    <div className="relative w-full xl:block hidden">
                        <div className="absolute top-[-100px] sm:top-[-200px] md:top-[-300px] lg:top-[-60px] lg:right-[150px] rotate-180 md:rotate-90 lg:rotate-0 right-[-100px] sm:right-[-200px] pointer-events-none z-0 opacity-30 sm:opacity-50 lg:opacity-100">
	                            <img
	                                src="/features_lines.svg"
	                                alt="Illustration of the StudyStash teaching-learning loop"
	                                className="w-[400px] sm:w-[600px] h-auto max-w-none lg:scale-150"
	                                loading="lazy"
	                                decoding="async"
	                            />
                        </div>
                    </div>

                    <div className="relative w-full pt-[15px] sm:pt-[50px] pb-8 lg:pt-[200px]">
                        <IntegrationsCarouselCompact />
                    </div>

                    <div className="relative w-full pt-[15px] md:pt-[170px]">
                        <VideosWithFeatures section_refs={featureSectionRefs} />
                    </div>

                    <div className="pt-6 sm:pt-0 pb-12 sm:pb-24 w-full">
                        <EvidenceSection
                            source={
                                <>
                                    Source - UOB pilot 2025 <span className="text-[8px] sm:text-[9px] md:text-[10px]">n=2,192</span>
                                </>
                            }
                            circles={[
                                {
                                    value: "59%",
                                    title: t("home.evidence.circles.circle-1.title"),
                                    description: "Of students reported StudyStash helped improve their academic performance"
                                },
                                {
                                    value: "74%",
                                    title: "Student Confidence",
                                    description: "Of students reported increased confidence managing study workload"
                                },
                                {
                                    value: "34%",
                                    title: "Student Engagement",
                                    description: locale === 'en-us' ? "Of students regularly used StudyStash outside of class time (avg. 3 hours 20 mins/week)" : "Of students regularly used StudyStash outside of lecture time (avg. 3 hours 20 mins/week)"
                                }
                            ]}
                        />
                    </div>

                    <div className="relative w-full pt-12 sm:pt-16 md:pt-24">
                        {/* Background SVG for LeftRightSection */}
                        <div className="absolute left-0 top-[-30px] sm:top-[-50px] md:top-[240px] w-full h-1/2 pointer-events-none z-0 opacity-60 sm:opacity-80 md:opacity-100">
	                            <img
	                                src="/left-right-loops.svg"
	                                alt="Illustration of the StudyStash teaching-learning loop"
	                                className="w-full h-full object-contain"
	                                loading="lazy"
	                                decoding="async"
	                            />
                        </div>

                        {/* Background SVG for LeftRightSection */}
                        <div className="absolute left-0 md:hidden block top-[600px] sm:top-[900px] w-full h-1/2 pointer-events-none z-0 opacity-40 sm:opacity-60">
	                            <img
	                                src="/left-right-loops.svg"
	                                alt="Illustration of the StudyStash teaching-learning loop"
	                                className="w-full h-full object-contain"
	                                loading="lazy"
	                                decoding="async"
	                            />
                        </div>

                        <div className="relative z-10">
                            <AlternatingSection
                                sections={[
                                    {
                                        title: "Ready to complete your teaching-learning loop?",
                                        content: "Traditional teaching methods don't always match how students actually learn, but this gap doesn't have to be inevitable. With StudyStash, you can transform static content into adaptive learning experiences, turn guesswork about student struggles into actionable insights, and deliver measurable improvements in student outcomes.",
                                        image: {
                                            src: "/college-students.jpg",
                                            alt: "Teacher engaging with students in classroom"
                                        }
                                    },
                                    {
                                        title: `Start with a pilot ${t("general.words.programme")}`,
                                        content: (
                                            <>
                                                <p>
                                                    Choose 2-3 {t("general.words.modules")}, witness the impact on student {t("general.words.attainment")}
                                                    and lecturer insights, then scale what works. No complex
                                                    integrations, no staff training requirements, no disruption to
                                                    existing workflows.
                                                </p>
                                                <p>
                                                    Join universities already proving that evidence-backed,
                                                    student-{t("general.words.centred")} learning isn&apos;t just better for students - it&apos;s a
                                                    game-changer for everyone.
                                                </p>
                                            </>
                                        ),
                                        image: {
                                            src: "/stock1.jpg",
                                            alt: "Professional woman in orange blazer presenting"
                                        },
                                        button: {
                                            text: `Request your pilot ${t("general.words.programme")}`,
                                            variant: "primary" as const,
                                            size: "lg" as const,
                                            onClick: () => window.location.href = '/pilot-program'
                                        }
                                    }
                                ]}
                            />
                        </div>
                    </div>

                    <div className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 w-full max-w-7xl">
                        <QuoteCarousel quotes={exampleQuotes} />
                    </div>

                    <div className="pt-12 sm:pt-16 md:pt-24 pb-8 sm:pb-16 overflow-visible px-4 sm:px-6 lg:px-8  max-w-7xl mx-auto">
                        <ContentDownload />
                    </div>

                    <div className="py-12 sm:py-16 md:py-24">
                        <DynamicCaseStudies items={RecommendedBlogs} />
                    </div>
                </div>
            </div>
        </main>
    );
}
